import { Link } from '../../../components/Link';
import { Seo } from '../../../lib/seo';
import { LegalLayout } from '../../../components/LegalLayout';
import { useLegal } from './useLegal';

/** AUP — the spam-protection clause that lets you ban abusers without litigation. */
export function AupPage() {
  const c = useLegal();
  return (
    <><Seo title="Acceptable Use Policy" description="What you can and cannot do with SieveGuard — permitted use of the verification and warmup tools, prohibited activity, and how we act on reports of abuse." path="/aup" /><LegalLayout title="Acceptable Use Policy" lastUpdated={c.lastUpdatedAt}>
      <p>
        This Acceptable Use Policy (&quot;<strong>AUP</strong>&quot;) governs your use of
        the SieveGuard service. It supplements the <Link to="/terms">Terms of Use</Link>; breach
        of this AUP is grounds for immediate suspension or termination, without refund.
      </p>

      <h2>Prohibited use</h2>
      <p>You must not use SieveGuard to:</p>
      <ul>
        <li>
          <strong>Validate stolen, scraped, leaked, or otherwise unlawfully obtained
          email lists</strong>. Submitting personal data you do not have a lawful basis to
          process violates GDPR and this AUP.
        </li>
        <li>
          <strong>Send or facilitate spam, unsolicited commercial email, or any messaging
          that contravenes CAN-SPAM, CASL, GDPR, ePrivacy, or local equivalents.</strong>
          SieveGuard is a verification + integrity tool — it does not send mail and must not be
          used as part of a spam-sending workflow.
        </li>
        <li>
          <strong>Run political-spam targeting</strong>, mass voter contact campaigns based
          on non-opted-in data, or election interference activities.
        </li>
        <li>
          <strong>Harass, defame, dox, or threaten</strong> any individual or group.
        </li>
        <li>
          <strong>Bypass, probe, or attempt to defeat any rate limit, quota, trial gate,
          credit cap, or other access control.</strong>
        </li>
        <li>
          <strong>Reverse-engineer, decompile, or attempt to extract the source code or
          underlying scoring logic</strong> of the Service.
        </li>
        <li>
          <strong>Resell, white-label, sub-license, or operate the Service as a
          timeshare or service-bureau</strong> without our prior written consent.
        </li>
        <li>
          <strong>Build a competing product</strong> using the Service or data derived from it.
        </li>
        <li>
          <strong>Upload malware, viruses, scripts that exploit any vulnerability</strong>, or
          otherwise attempt to compromise the integrity of the Service or its users.
        </li>
        <li>
          <strong>Misrepresent your identity or affiliation</strong> in API requests, billing
          information, or support tickets.
        </li>
      </ul>

      <h2>Required lawful basis</h2>
      <p>
        Before submitting any email address to SieveGuard, you must have a lawful basis under
        GDPR Art. 6 (typically contract, legitimate interests, or consent) to process that
        person&apos;s data. We act as your Processor — your lawfulness flows through to us.
        Submitting a list you cannot lawfully process means you are using SieveGuard unlawfully.
      </p>

      <h2>Investigations and enforcement</h2>
      <p>
        We may investigate suspected AUP breaches and may suspend access while we do so.
        Where we have a good-faith belief that a serious breach has occurred (e.g. a
        large-scale stolen-list verification, sustained spam tooling), we may terminate the
        Subscription without refund and report to the relevant supervisory authority.
      </p>

      <h2>Reporting abuse</h2>
      <p>
        If you believe a SieveGuard customer is using the Service in violation of this AUP, please
        email <code>{c.supportEmail}</code> with details. We treat reports confidentially.
      </p>
    </LegalLayout></>
  );
}

// Default export required by React Router framework-mode route modules.
export default AupPage;
